
function dissectWatchable(w) {
    if (!(w instanceof Array)) {
        w = [w];
    }
    const model = w.shift();
    const events = w.length === 0 ? "all" : w.join(" ");
    return {model, events};
}

/**
 * I encapsulate watching a set of Watchables (e.g., Backbone.Model) for a set
 * of events ("all" by default), and doing "something". The "something" may also
 * register one-shot Watchables which will also trigger doing "something". The
 * one-shot Watchables operate via Backbone.Events.once, and have the same
 * semantics across the entire ModelSubscription: as soon as one fires, they're
 * all unregistered. Of course, since they were registered by the "something",
 * and they do "something" when one fires, chances are good they'll be re-added
 * very shortly.
 *
 * Each Watchable is either a single object mixing in Backbone.Events, or an
 * array with the zeroth element being an object mixing in Backbone.Events and
 * the remaining elements being specific events to watch. In the former case, or
 * no events are enumerated in the latter case, the "all" event will be used.
 *
 * Note that the registerd callback is _not_ provided any information about what
 * changed, just that something changed. This is in the Flux and React style,
 * as that is the target consumer. If you care about more granular updates or
 * avoiding recalculation, it's trivial to memoize within the callback, but it's
 * up to the client to decide if it's necessary.
 *
 * One little wrinkle here is that because Backbone's event dispatch is
 * synchronous, if there are multiple events which cause an update in the same
 * tick the callback will be invoked multiple times. This can result in a lot of
 * needless work, so one-shot listeners are unregistered before being registered
 * which cuts down on a lot (but not all) of the reentrant calculations.
 */
class ModelSubscriptions {

    constructor() {
        this.watchables = null;
        this.oneShots = [];
    }

    setCallback(callback) {
        this.listener = () => {
            this._resetOneShots();
            return callback();
        };
    }

    setWatchables(watchables) {
        this.reset();
        // wire model listeners
        this.watchables = watchables.map(w => {
            const {model, events} = dissectWatchable(w);
            model.on(events, this.listener);
            return {model, events};
        });
    }

    addOneShotWatchable(watchable) {
        // wire one-shot listener
        const {model, events} = dissectWatchable(watchable);
        model.off(events, this.listener);
        model.once(events, this.listener);
        this.oneShots.push({model, events});
    }

    reset() {
        this._resetModels();
        this._resetOneShots();
    }

    _resetModels() {
        if (this.watchables) {
            for (const {model, events} of this.watchables) {
                model.off(events, this.listener);
            }
            this.watchables = null;
        }
    }

    _resetOneShots() {
        for (const {model, events} of this.oneShots) {
            model.off(events, this.listener);
        }
        this.oneShots = [];
    }
}

export default ModelSubscriptions;



// WEBPACK FOOTER //
// ./app/data/util/ModelSubscriptions.js